"use client";

import React from "react";
import { Leaf, Droplets, FlaskConical, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const steps = [{
  title: "Ancient Soil",
  description: "Our vines thrive in Kimmeridgian limestone—a prehistoric seabed that imparts a distinct mineral tension and crisp acidity. [45% Clay / 55% Calcium]",
  icon: Leaf
}, {
  title: "Micro-Climate",
  description: "Nestled in a rain shadow, the vineyard enjoys 2,200 solar hours annually, balanced by cool Pacific breezes. [Avg Temp: 18°C / Rainfall: 650mm]",
  icon: Droplets
}, {
  title: "Topography",
  description: "Planted on steep south-facing slopes at 420m elevation, our blocks receive optimal solar radiation for phenolic ripeness. [15% Gradient / SSW Aspect]",
  icon: FlaskConical
}, {
  title: "Viticulture",
  description: "High-density organic farming forces roots deep into the bedrock, capturing the true essence of our estate's unique geology. [Root Depth: 4m / 35yr Vines]",
  icon: Truck
}];
export default function TheBlissProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="36798" id="the-terroir" ref={ref} className="bg-background text-foreground py-24 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tight mb-4 text-primary">
            The Terroir
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-sans tracking-wide">
            A symphony of soil, sun, and slope. Discover the unique geological blueprint that defines the character of VineEssence wines.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {steps.map((step, i) => {
            return <Card key={step.title} data-index={i} className={cn("bg-card/50 backdrop-blur-sm border-secondary/20 hover:border-secondary/40 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", i === 0 && "delay-100", i === 1 && "delay-300", i === 2 && "delay-500", i === 3 && "delay-700")}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-background border border-secondary/30 shadow-sm relative">
                        <step.icon className="w-5 h-5 text-secondary" strokeWidth={1.5} />
                        <div className="absolute inset-0 rounded-full border border-secondary/20 animate-ping opacity-10" />
                      </div>
                      <CardTitle className="text-2xl font-serif font-semibold text-primary italic">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-muted-foreground leading-relaxed font-sans tracking-wide">
                      {step.description.split('[').map((part, idx) => idx === 0 ? <p key={idx} className="mb-4">{part}</p> : <span key={idx} className="block mt-2 font-mono text-xs uppercase tracking-widest text-secondary bg-secondary/5 p-2 rounded-sm border border-secondary/10">
                            {`[${part}`}
                          </span>)}
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}